
import { useState, useEffect, useRef, useCallback } from "react";
import { EnhancedSearchService } from "@/services/searchService";
import { toast } from "sonner";

interface SearchFilters {
  categoryFilter?: string;
  difficultyFilter?: string;
  timeFilter?: string;
  tagFilters?: string[];
  sortBy?: 'relevance' | 'popular' | 'recent' | 'alphabetical';
}

interface UseEnhancedSearchProps {
  initialQuery?: string;
  initialFilters?: SearchFilters;
  debounceMs?: number;
  autoSearch?: boolean;
}

export const useEnhancedSearch = ({
  initialQuery = "",
  initialFilters = {},
  debounceMs = 300,
  autoSearch = true
}: UseEnhancedSearchProps = {}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [results, setResults] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const suggestionsTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const lastSearchId = useRef<string>("");

  // Load search history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('promptSearchHistory');
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error('Error loading search history:', error);
      }
    }
  }, []);

  // Save search to history
  const saveToHistory = useCallback((query: string) => {
    if (!query.trim() || query.length < 3) return;
    
    setSearchHistory(prev => {
      const newHistory = [query, ...prev.filter(h => h !== query)].slice(0, 10);
      localStorage.setItem('promptSearchHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  // Get search suggestions
  const getSuggestions = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    
    // Clear previous timeout
    if (suggestionsTimeout.current) {
      clearTimeout(suggestionsTimeout.current);
    }

    suggestionsTimeout.current = setTimeout(async () => {
      try {
        const suggestions = await EnhancedSearchService.getSearchSuggestions(query, 5);
        setSuggestions(suggestions);
      } catch (error) {
        console.error('Error getting suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 200);
  }, []);

  // Perform search
  const performSearch = useCallback(async (query: string, searchFilters: SearchFilters) => {
    if (!query.trim() && !searchFilters.categoryFilter) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    const searchId = Date.now().toString();
    lastSearchId.current = searchId;

    // Cancel previous request
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    setIsLoading(true);

    try {
      const searchResults = await EnhancedSearchService.searchPrompts(query, {
        ...searchFilters,
        limit: 50
      });

      // Check if this is still the latest search
      if (searchId === lastSearchId.current) {
        setResults(searchResults);
        if (query.trim()) {
          saveToHistory(query);
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError' && searchId === lastSearchId.current) {
        console.error('Search error:', error);
        toast.error('Search failed. Please try again.');
        setResults([]);
      }
    } finally {
      if (searchId === lastSearchId.current) {
        setIsLoading(false);
      }
    }
  }, [saveToHistory]);

  // Debounced search effect
  useEffect(() => {
    if (!autoSearch) return;

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout
    searchTimeout.current = setTimeout(() => {
      performSearch(searchQuery, filters);
    }, debounceMs);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery, filters, debounceMs, autoSearch, performSearch]);

  // Get suggestions when query changes
  useEffect(() => {
    getSuggestions(searchQuery);
  }, [searchQuery, getSuggestions]);

  // Handle search query change
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Handle filter change
  const handleFilterChange = useCallback((newFilters: Partial<SearchFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setResults([]);
    setSuggestions([]);
    setFilters({});
  }, []);

  // Clear search history
  const clearSearchHistory = useCallback(() => {
    setSearchHistory([]);
    localStorage.removeItem('promptSearchHistory');
  }, []);

  // Manual search trigger
  const search = useCallback((query?: string, searchFilters?: SearchFilters) => {
    const queryToUse = query !== undefined ? query : searchQuery;
    const filtersToUse = searchFilters !== undefined ? searchFilters : filters;
    
    performSearch(queryToUse, filtersToUse);
  }, [searchQuery, filters, performSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      if (suggestionsTimeout.current) clearTimeout(suggestionsTimeout.current);
      if (abortController.current) abortController.current.abort();
    };
  }, []);

  return {
    searchQuery,
    filters,
    results,
    suggestions,
    searchHistory,
    isLoading,
    isLoadingSuggestions,
    handleSearchChange,
    handleFilterChange,
    clearSearch,
    clearSearchHistory,
    search,
    resultCount: results.length
  };
};
